import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  AlertCircle,
  FileText,
  Plus,
  Search,
  UserCheck,
  UserX,
  Wallet,
  XCircle,
} from "lucide-react"
import { type FormEvent, useEffect, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { CLAVES_INGRESO, DialogoIngreso } from "@/componentes/ingresos/DialogoIngreso"
import { DialogoPagador, DialogoQuitarPagador } from "@/componentes/ingresos/DialogoPagador"
import { EstadoCuentaTercero } from "@/componentes/ingresos/EstadoCuentaTercero"
import { type Ingreso, NOMBRE_TIPO, queEsElResto } from "@/componentes/ingresos/tipos"
import { KpiIngresos } from "@/componentes/kpi/KpiTesoreria"
import { aCentimos, monto } from "@/componentes/tesoreria/dinero"
import { EstadoDeCuenta } from "@/componentes/tesoreria/EstadoCuentaProveedor"
import { Boton } from "@/componentes/ui/Boton"
import { MenuAcciones, type OpcionMenu } from "@/componentes/ui/MenuAcciones"
import { Modal } from "@/componentes/ui/Modal"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { type ArchivoParaVer, VistaPreviaArchivo } from "@/componentes/ui/VistaPreviaArchivo"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

import { CuentasClientes, CuentasEmpleados } from "./CuentasTerceros"

const ESTADOS = [
  { valor: "", etiqueta: "Todos" },
  { valor: "POR_DETERMINAR", etiqueta: "Por determinar" },
  { valor: "ASIGNADO", etiqueta: "Con pagador" },
  { valor: "ANULADO", etiqueta: "Anulados" },
] as const

const SECCIONES = [
  { valor: "reportados", etiqueta: "Ingresos reportados" },
  { valor: "clientes", etiqueta: "Cuentas de clientes" },
  { valor: "empleados", etiqueta: "Cuentas de empleados" },
] as const

type Dialogo =
  | { que: "asignar" | "aplicar" | "quitar" | "anular"; ingreso: Ingreso }
  | { que: "cuenta"; ingreso: Ingreso }
  | null

/**
 * Tesorería → «Ingresos»: el dinero que entra, con su soporte y su pagador (14/10).
 *
 * ⚠ **El pagador puede quedar POR DETERMINAR** y se le asigna después: el ingreso entra
 * al banco al momento aunque todavía no se sepa quién lo mandó. Esa es la lista que hay
 * que vaciar, y por eso tiene su propio filtro.
 *
 * ⚠ **Anular un ingreso es anular su movimiento**, con su motivo: se deshace también lo
 * que hizo en la cuenta del pagador. Quitar el pagador NO anula: el dinero sí entró.
 */
export function Ingresos() {
  const usuario = usarSesion((e) => e.usuario)
  const [seccion, setSeccion] = useState<(typeof SECCIONES)[number]["valor"]>("reportados")
  const [reportando, setReportando] = useState(false)
  const puedeRegistrar = tienePermiso(usuario, "ingreso:registrar")

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <fieldset className="flex flex-wrap gap-2">
          <legend className="sr-only">Qué ver</legend>
          {SECCIONES.map((s) => (
            <Boton
              key={s.valor}
              tamano="sm"
              variante={seccion === s.valor ? "primario" : undefined}
              aria-pressed={seccion === s.valor}
              onClick={() => setSeccion(s.valor)}
            >
              {s.etiqueta}
            </Boton>
          ))}
        </fieldset>
        {puedeRegistrar && (
          <Boton variante="primario" onClick={() => setReportando(true)}>
            <Plus size={16} /> Reportar ingreso
          </Boton>
        )}
      </div>
      <KpiIngresos />
      {seccion === "reportados" && <Reportados />}
      {seccion === "clientes" && <CuentasClientes />}
      {seccion === "empleados" && <CuentasEmpleados />}
      {reportando && <DialogoIngreso onCerrar={() => setReportando(false)} />}
    </section>
  )
}

function Reportados() {
  const usuario = usarSesion((e) => e.usuario)
  const [estado, setEstado] = useState<string>("")
  const [texto, setTexto] = useState("")
  const [buscar, setBuscar] = useState("")
  const [dialogo, setDialogo] = useState<Dialogo>(null)
  const [viendo, setViendo] = useState<ArchivoParaVer | null>(null)

  useEffect(() => {
    const espera = window.setTimeout(() => setBuscar(texto.trim()), 300)
    return () => window.clearTimeout(espera)
  }, [texto])

  const ingresos = useQuery({
    queryKey: ["ingresos", estado, buscar],
    queryFn: () => {
      const q = new URLSearchParams()
      if (estado) q.set("estado", estado)
      if (buscar) q.set("buscar", buscar)
      return api.get<Ingreso[]>(`/api/v1/ingresos?${q}`)
    },
  })
  const puedeRegistrar = tienePermiso(usuario, "ingreso:registrar")
  const puedeAnular = tienePermiso(usuario, "tesoreria:anular")

  function opciones(i: Ingreso): OpcionMenu[] {
    const lista: OpcionMenu[] = []
    if (i.soportes[0]) {
      const s = i.soportes[0]
      lista.push({
        id: "soporte",
        etiqueta: "Ver soporte",
        icono: <FileText size={16} />,
        onSelect: () =>
          setViendo({
            ruta: `/api/v1/transacciones/${i.transaccion_id}/soportes/${s.id}/contenido`,
            nombre: s.nombre_original,
          }),
      })
    }
    if (i.pagador_tipo !== null)
      lista.push({
        id: "cuenta",
        etiqueta: "Ver la cuenta del pagador",
        icono: <Wallet size={16} />,
        onSelect: () => setDialogo({ que: "cuenta", ingreso: i }),
      })
    if (i.anulado || !puedeRegistrar) return lista
    if (i.pagador_tipo === null)
      lista.push({
        id: "asignar",
        etiqueta: "Asignar pagador",
        icono: <UserCheck size={16} />,
        onSelect: () => setDialogo({ que: "asignar", ingreso: i }),
      })
    if (i.pagador_tipo === "CLIENTE" && aCentimos(i.resto) > 0n)
      lista.push({
        id: "aplicar",
        etiqueta: "Aplicar anticipo a sus CxC",
        icono: <Wallet size={16} />,
        onSelect: () => setDialogo({ que: "aplicar", ingreso: i }),
      })
    if (i.pagador_tipo !== null)
      lista.push({
        id: "quitar",
        etiqueta: "Quitar pagador",
        ayuda: "Deshace lo que hizo en su cuenta; el dinero sigue en el banco",
        icono: <UserX size={16} />,
        onSelect: () => setDialogo({ que: "quitar", ingreso: i }),
      })
    if (puedeAnular)
      lista.push({
        id: "anular",
        etiqueta: "Anular ingreso",
        ayuda: "Anula el movimiento del banco y lo que hizo en la cuenta",
        icono: <XCircle size={16} />,
        peligrosa: true,
        separador: true,
        onSelect: () => setDialogo({ que: "anular", ingreso: i }),
      })
    return lista
  }

  const subtitulo = [
    ESTADOS.find((e) => e.valor === estado)?.etiqueta ?? "Todos",
    buscar ? `Búsqueda «${buscar}»` : null,
  ]
    .filter(Boolean)
    .join(" · ")

  // El monto va con su moneda en una columna aparte: en la pantalla se mezclan
  // bolívares, dólares y USDT, y una columna de cifras sin moneda sería un dato falso.
  const documento = (): DocumentoExportable => ({
    titulo: "Ingresos reportados",
    subtitulo,
    secciones: [
      {
        titulo: "Ingresos reportados",
        columnas: [
          { titulo: "Fecha", tipo: "fecha" },
          { titulo: "Código", tipo: "texto" },
          { titulo: "Concepto", tipo: "texto" },
          { titulo: "Referencia", tipo: "texto" },
          { titulo: "Pagador", tipo: "texto" },
          { titulo: "Tipo de pagador", tipo: "texto" },
          { titulo: "Estado", tipo: "texto" },
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "Monto", tipo: "monto" },
          { titulo: "Aplicado en su cuenta", tipo: "monto" },
          { titulo: "Resto", tipo: "monto" },
        ],
        filas: (ingresos.data ?? []).map((i) => ({
          celdas: [
            i.fecha_valor,
            i.codigo,
            i.concepto,
            i.referencia,
            i.pagador_tipo === null ? "Por determinar" : i.pagador,
            i.pagador_tipo === null ? null : NOMBRE_TIPO[i.pagador_tipo],
            i.anulado ? `Anulado: ${i.motivo_anulacion ?? ""}`.trim() : null,
            i.divisa,
            Number(i.monto),
            i.pagador_tipo === null ? null : Number(i.aplicado),
            i.pagador_tipo === null ? null : Number(i.resto),
          ],
        })),
      },
    ],
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <fieldset className="flex flex-wrap gap-2">
          <legend className="sr-only">Estado</legend>
          {ESTADOS.map((e) => (
            <Boton
              key={e.valor}
              tamano="sm"
              variante={estado === e.valor ? "primario" : undefined}
              aria-pressed={estado === e.valor}
              onClick={() => setEstado(e.valor)}
            >
              {e.etiqueta}
            </Boton>
          ))}
        </fieldset>
        <div className="relative sm:w-72">
          <Search
            size={16}
            aria-hidden={true}
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
            style={{ color: "var(--fg-muted)" }}
          />
          <input
            aria-label="Buscar por código, concepto o referencia"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Código, concepto o referencia…"
            className="campo pl-9"
          />
        </div>
      </div>
      {(ingresos.data?.length ?? 0) > 0 && (
        <div className="flex flex-wrap justify-end gap-2">
          <Exportar titulo="Ingresos reportados" subtitulo={subtitulo} documento={documento} />
        </div>
      )}

      {ingresos.isError ? (
        <p className="flex items-center gap-2 py-6" style={{ color: "var(--destructive)" }}>
          <AlertCircle size={16} /> No se pudieron cargar los ingresos.
        </p>
      ) : (
        <Tabla etiqueta="Ingresos reportados">
          <Cabecera>
            <Th>Fecha</Th>
            <Th>Ingreso</Th>
            <Th>Pagador</Th>
            <Th derecha>Monto</Th>
            <Th derecha>En su cuenta</Th>
            <Th>
              <span className="sr-only">Acciones</span>
            </Th>
          </Cabecera>
          <Cuerpo>
            {ingresos.isPending && <TablaVacia colSpan={6}>Cargando…</TablaVacia>}
            {ingresos.data?.length === 0 && (
              <TablaVacia colSpan={6}>
                {estado === "POR_DETERMINAR"
                  ? "No hay ingresos por determinar: todos tienen su pagador."
                  : "No hay ingresos reportados con ese filtro."}
              </TablaVacia>
            )}
            {ingresos.data?.map((i) => (
              <Fila key={i.id}>
                <Td numero>{comoFecha(i.fecha_valor)}</Td>
                <Td>
                  <span
                    className={`block font-medium tabular-nums ${i.anulado ? "line-through" : ""}`}
                  >
                    {i.codigo}
                  </span>
                  <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                    {i.concepto}
                    {i.referencia ? ` · ref. ${i.referencia}` : ""}
                  </span>
                  {i.anulado && (
                    <span className="block text-xs" style={{ color: "var(--destructive)" }}>
                      Anulado: {i.motivo_anulacion}
                    </span>
                  )}
                </Td>
                <Td>
                  {i.pagador_tipo === null ? (
                    <span className="text-sm font-medium" style={{ color: "var(--warning)" }}>
                      Por determinar
                    </span>
                  ) : (
                    <>
                      <span className="block">{i.pagador}</span>
                      <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                        {NOMBRE_TIPO[i.pagador_tipo]}
                      </span>
                    </>
                  )}
                </Td>
                <Td derecha numero>
                  {monto(i.monto)} {i.divisa}
                </Td>
                <Td derecha numero>
                  {i.pagador_tipo === null ? (
                    "—"
                  ) : (
                    <>
                      {aCentimos(i.aplicado) > 0n && (
                        <span className="block">
                          {i.pagador_tipo === "CLIENTE" ? "Cobrado" : "Devolución"}{" "}
                          {monto(i.aplicado)}
                        </span>
                      )}
                      {aCentimos(i.resto) > 0n && (
                        <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                          {queEsElResto(i.pagador_tipo)} {monto(i.resto)}
                        </span>
                      )}
                    </>
                  )}
                </Td>
                <Td derecha>
                  <MenuAcciones opciones={opciones(i)} />
                </Td>
              </Fila>
            ))}
          </Cuerpo>
        </Tabla>
      )}

      {dialogo && (dialogo.que === "asignar" || dialogo.que === "aplicar") && (
        <DialogoPagador
          ingreso={dialogo.ingreso}
          modo={dialogo.que}
          onCerrar={() => setDialogo(null)}
        />
      )}
      {dialogo?.que === "quitar" && (
        <DialogoQuitarPagador ingreso={dialogo.ingreso} onCerrar={() => setDialogo(null)} />
      )}
      {dialogo?.que === "anular" && (
        <DialogoAnularIngreso ingreso={dialogo.ingreso} onCerrar={() => setDialogo(null)} />
      )}
      {dialogo?.que === "cuenta" &&
        dialogo.ingreso.pagador_id !== null &&
        (dialogo.ingreso.pagador_tipo === "PROVEEDOR" ? (
          <EstadoDeCuenta
            proveedorId={dialogo.ingreso.pagador_id}
            nombre={dialogo.ingreso.pagador ?? ""}
            onCerrar={() => setDialogo(null)}
          />
        ) : (
          <EstadoCuentaTercero
            tipo={dialogo.ingreso.pagador_tipo === "CLIENTE" ? "CLIENTE" : "EMPLEADO"}
            id={dialogo.ingreso.pagador_id}
            nombre={dialogo.ingreso.pagador ?? ""}
            onCerrar={() => setDialogo(null)}
          />
        ))}
      <VistaPreviaArchivo archivo={viendo} onCerrar={() => setViendo(null)} />
    </div>
  )
}

const MOTIVO_MINIMO = 10

/** Anular el ingreso es anular su movimiento: la cascada deshace lo demás. */
function DialogoAnularIngreso({ ingreso, onCerrar }: { ingreso: Ingreso; onCerrar: () => void }) {
  const clienteQuery = useQueryClient()
  const [motivo, setMotivo] = useState("")
  const anular = useMutation({
    meta: { exito: "Ingreso anulado" },
    mutationFn: () =>
      api.post(`/api/v1/transacciones/${ingreso.transaccion_id}/anular`, {
        motivo: motivo.trim(),
      }),
    onSuccess: () => {
      for (const clave of CLAVES_INGRESO) clienteQuery.invalidateQueries({ queryKey: [...clave] })
      onCerrar()
    },
  })
  const listo = motivo.trim().length >= MOTIVO_MINIMO
  return (
    <Modal
      abierto
      titulo="Anular ingreso"
      descripcion={`${ingreso.codigo} · ${monto(ingreso.monto)} ${ingreso.divisa}`}
      onCerrar={onCerrar}
    >
      <form
        className="space-y-4"
        onSubmit={(e: FormEvent) => {
          e.preventDefault()
          if (listo) anular.mutate()
        }}
      >
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          El movimiento del banco queda tachado y deja de contar en el saldo.
          {ingreso.pagador_tipo !== null &&
            " También se anulan los cobros y la devolución que hizo en la cuenta del pagador."}
        </p>
        <label className="block space-y-1 text-sm">
          <span className="font-medium">Motivo</span>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            maxLength={300}
            rows={3}
            className="campo"
          />
        </label>
        {anular.error instanceof ErrorApi && (
          <p className="text-sm" style={{ color: "var(--destructive)" }}>
            {anular.error.message}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Boton onClick={onCerrar}>Cancelar</Boton>
          <Boton type="submit" variante="peligro" disabled={!listo || anular.isPending}>
            {anular.isPending ? "Anulando…" : "Anular ingreso"}
          </Boton>
        </div>
      </form>
    </Modal>
  )
}
